import { useIdentify } from "@fideus/analytics";
import { type ReactNode, useContext, useEffect, useState } from "react";
import type { AuthClient } from "../AuthClient";
import { authContext } from "../session/authContext";
import type { User } from "../User";
import { signedUpContext } from "./signedUpContext";

function AcceptInviteRedirect({
  acceptInvite,
  inviteId,
  loadingFallback,
}: {
  acceptInvite: AuthClient["acceptInvite"];
  inviteId: string;
  loadingFallback: ReactNode;
}) {
  useEffect(() => {
    void acceptInvite({ inviteId });
  }, [acceptInvite, inviteId]);

  return loadingFallback;
}

interface LoadingState {
  state: "loading";
}

interface AuthenticatedState {
  state: "authenticated";
  user: User;
}

interface UnauthenticatedState {
  state: "unauthenticated";
}
/** @LearningJourney Authentication 12 - Wrap protected routes; provides user via signedUpContext */
export function SignedUpGuard({
  children,
  loadingFallback,
}: {
  children: ReactNode;
  loadingFallback: ReactNode;
}) {
  const auth = useContext(authContext);
  if (!auth) {
    throw new Error("SignedUpGuard can only be used in an AuthProvider");
  }
  const authClient = auth.client;

  const [authState, setAuthState] = useState<
    LoadingState | AuthenticatedState | UnauthenticatedState
  >({
    state: "loading",
  });

  const identify = useIdentify();

  useEffect(() => {
    async function checkAuth() {
      setAuthState({ state: "loading" });
      if (await authClient.isLoggedIn()) {
        const user = await authClient.getUser();
        await identify({ id: user.id });
        setAuthState({
          state: "authenticated",
          user,
        });
      } else {
        setAuthState({ state: "unauthenticated" });
      }
    }

    void checkAuth();
  }, [authClient, identify]);

  if (authState.state === "loading") {
    return loadingFallback;
  }

  if (authState.state === "unauthenticated") {
    const inviteId = new URLSearchParams(window.location.search).get(
      "inviteId"
    );
    if (inviteId) {
      return (
        <AcceptInviteRedirect
          acceptInvite={authClient.acceptInvite.bind(authClient)}
          inviteId={inviteId}
          loadingFallback={loadingFallback}
        />
      );
    }
    return auth.loginFallback;
  }

  return (
    <signedUpContext.Provider value={{ user: authState.user }}>
      {children}
    </signedUpContext.Provider>
  );
}
